<script setup lang="ts">import { toRef as _toRef, ref as _ref } from 'vue';

import {watch} from 'vue'
import LoadingComponent from '@toolify/client/src/modules/ui/components/LoadingComponent.vue'
import FullWidthCenterComponent from '@toolify/client/src/modules/ui/components/FullWidthCenterComponent.vue'
import ScrollbarComponent from '@toolify/client/src/modules/ui/components/ScrollbarComponent.vue'
import {useIntersectionObserver} from '@vueuse/core'

import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'
import {useThemeVariables} from '@toolify/client/src/modules/theme/composables/useThemeVariables'

interface Props {
  shouldShowLoadMore: boolean
  isLoading?: boolean
  gradientColor?: string
}
const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  gradientColor: null,
})

interface Emits {
  (e: 'loadMoreVisibilityChange', value: boolean): void
  (e: 'scroll', value: {
    x: number
    y: number
  }): void
}
const emit = defineEmits<Emits>()

const __$temp_1 = (useThemeVariables()),
  getThemeVariable = _toRef(__$temp_1, 'getThemeVariable');

let targetIsVisible = _ref<boolean>(false)
const targetRef = _ref<HTMLDivElement>()

useIntersectionObserver(
  (targetRef),
  ([{isIntersecting}]) => {
    targetIsVisible.value = isIntersecting
    emit('loadMoreVisibilityChange', isIntersecting)
  },
)

const scrollbarRef = _ref(null)
const contentRef = _ref<HTMLDivElement>()
let contentHeightBeforeLoad = 0

watch(() => props.isLoading, (isLoading) => {
  const contentHeight = contentRef.value?.offsetHeight ?? 0
  if (isLoading) {
    contentHeightBeforeLoad = contentHeight
    return
  }
  if (contentHeight > contentHeightBeforeLoad && props.shouldShowLoadMore && targetIsVisible.value) {
    emit('loadMoreVisibilityChange', true)
  }
}, {
  flush: 'post',
})

defineExpose({
  scrollToBottom: () => {
    scrollbarRef.value?.scrollToBottom()
  },
  scrollToPercentage: (percentage: number) => {
    scrollbarRef.value?.scrollToPercentage(percentage)
  },
  scrollByX: (x: number) => {
    scrollbarRef.value?.scrollByX(x)
  },
  scrollToX: (x: number) => {
    scrollbarRef.value?.scrollToX(x)
  },
  scrollByY: (y: number) => {
    scrollbarRef.value?.scrollByY(y)
  },
  scrollToY: (y: number) => {
    scrollbarRef.value?.scrollToY(y)
  },
  smoothScrollToY: (y: number) => {
    scrollbarRef.value?.smoothScrollToY(y)
  },
  getScrollHeight: () => {
    return scrollbarRef.value?.getScrollHeight()
  },
  getScrollTop: () => {
    return scrollbarRef.value?.getScrollTop() ?? 0
  },
})
</script>


<template>
  <div class="infinity-scroll">
    <ScrollbarComponent
      :gradientColor="gradientColor ?? getThemeVariable(ThemeVariableType.SurfaceBrightColor)"
      :options="{suppressScrollX: true}"
      ref="scrollbarRef"
      @scroll="(data) => emit('scroll', data)"
    >
      <div class="container" ref="contentRef">
        <slot/>
        <div ref="targetRef" v-if="shouldShowLoadMore">
          <slot name="loader">
            <FullWidthCenterComponent :padding="[30, 0]">
              <LoadingComponent/>
            </FullWidthCenterComponent>
          </slot>
        </div>
      </div>
    </ScrollbarComponent>
  </div>
</template>

<style scoped lang="scss">
  .infinity-scroll {
    width: 100%;
    height: 100%;
    min-height: 0;
  }

  .container{
    width: 100%;
  }
</style>
